/** HeroImageGenerator — small drop-in button that opens a modal
 *  taking 1-3 evocative keywords + an optional style modifier, then
 *  calls POST /api/orgs/hero-image/generate (Gemini Nano Banana).
 *
 *  The consumer wires:
 *    <HeroImageGenerator
 *      currentUrl={org.public_hero_image_url}
 *      onGenerated={(url) => setHero(url)}     // called with the new URL
 *    />
 *
 *  Renders inline as a button. Only shows the modal when clicked so
 *  it stays lightweight on the page.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Wand2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import api from "@/lib/api";

export default function HeroImageGenerator({ currentUrl = "", onGenerated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button"
        onClick={() => setOpen(true)}
        data-testid="hero-generate-button"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[10px] tracking-[0.24em] uppercase font-semibold text-black bg-[#F5B82E] hover:bg-[#f5c34e] transition">
        <Wand2 size={12} /> Generate with AI
      </button>
      {open && (
        <HeroImageDialog
          currentUrl={currentUrl}
          onClose={() => setOpen(false)}
          onGenerated={(url) => { onGenerated?.(url); setOpen(false); }}
        />
      )}
    </>
  );
}

function HeroImageDialog({ currentUrl, onClose, onGenerated }) {
  const [kw, setKw] = useState(["", "", ""]);   // exactly 3 slots
  const [style, setStyle] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const setKwAt = (i, v) => setKw((k) => k.map((x, idx) => (idx === i ? v : x)));

  const submit = async () => {
    const clean = kw.map((k) => k.trim()).filter(Boolean);
    if (clean.length === 0) return toast.error("Add at least one keyword.");
    setBusy(true);
    try {
      const { data } = await api.post("/orgs/hero-image/generate", {
        keywords:    clean,
        style:       style.trim() || null,
        aspect_ratio: "16:9",
      });
      if (!data.hero_url) throw new Error("No image returned");
      setPreview(data.hero_url);
      toast.success("Hero image ready. Save to publish.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4"
         onClick={onClose} data-testid="hero-generate-modal">
      <div className="w-full max-w-xl cs-card rounded-2xl p-6 sm:p-8"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F5B82E]/15 text-[#F5B82E] flex items-center justify-center">
              <Sparkles size={18}/>
            </div>
            <div>
              <div className="font-display text-lg uppercase tracking-tight">Generate Hero Image</div>
              <div className="text-[11px] text-white/55">Powered by Gemini Nano Banana · 3 keywords max</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10"
                  data-testid="hero-generate-close">
            <X size={16} className="text-white/60"/>
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <Input key={i}
                data-testid={`hero-keyword-${i}`}
                value={kw[i]}
                onChange={(e) => setKwAt(i, e.target.value)}
                placeholder={`Keyword ${i + 1}${i === 0 ? " · required" : ""}`}
                maxLength={40}
                className="bg-black/40 border-white/15"/>
            ))}
          </div>
          <div>
            <Input
              data-testid="hero-style"
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              placeholder="Optional style · e.g. 'cinematic teal-orange, wide-angle'"
              maxLength={80}
              className="bg-black/40 border-white/15"/>
          </div>

          {preview && (
            <div className="rounded-lg overflow-hidden border border-white/15"
                 data-testid="hero-preview">
              <img src={preview} alt="Generated hero preview"
                   className="w-full h-56 object-cover" />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              data-testid="hero-cancel"
              className="h-10 px-4 rounded-full border border-white/15 text-[10px] tracking-[0.24em] uppercase text-white/70 hover:text-white hover:border-white/40">
              Cancel
            </button>
            <Button type="button" onClick={submit}
              data-testid="hero-generate-submit"
              disabled={busy}
              className="btn-themed h-10 rounded-full text-[10px] tracking-[0.24em] uppercase font-semibold">
              {busy ? "Generating…" : preview ? "Regenerate" : "Generate"}
            </Button>
            {preview && (
              <Button type="button"
                onClick={() => onGenerated(preview)}
                data-testid="hero-use"
                className="h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-[10px] tracking-[0.24em] uppercase font-semibold">
                Use this
              </Button>
            )}
          </div>
        </div>

        {currentUrl && !preview && (
          <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-white/45"
               data-testid="hero-current-hint">
            Current hero remains active until you generate + save a new one.
          </div>
        )}
      </div>
    </div>
  );
}
